import {
  CONNECTION_REGEX,
  CustomAuthProperty,
  OAuth2Props,
  PieceAuthProperty,
  PieceMetadata,
  PieceMetadataModel,
  PieceMetadataModelSummary,
  PieceProperty,
  PiecePropertyMap,
  PropertyType,
  SwaggerFetchTokenAuthProperty,
} from '@activepieces/pieces-framework';
import {
  Action,
  ActionType,
  CodeActionSchema,
  isEmpty,
  LoopOnItemsActionSchema,
  PieceActionSchema,
  PieceActionSettings,
  PieceTrigger,
  PieceTriggerSettings,
  Trigger,
  TriggerType,
  isNil,
  spreadIfDefined,
  RouterActionSchema,
  RouterBranchesSchema,
  SampleDataSetting,
  RouterExecutionType,
  UpsertOAuth2Request,
  UpsertCloudOAuth2Request,
  UpsertPlatformOAuth2Request,
  UpsertAppConnectionRequestBody,
  UpsertCustomAuthRequest,
  UpsertBasicAuthRequest,
  UpsertSecretTextRequest,
  UpsertSwaggerFetchTokenAuthRequest,
  UpsertKhubOAuth2Request,
  FormInputType,
  buildDynamicPropertiesKey,
} from '@activepieces/shared';
import { TSchema, Type } from '@sinclair/typebox';
import { t } from 'i18next';
import { useEffect } from 'react';
import { ControllerRenderProps, useFormContext } from 'react-hook-form';

import { useBuilderStateContext } from '../builder-hooks';

import { FormError } from '@/components/ui/form';

function addAuthToPieceProps(
  props: PiecePropertyMap,
  auth: PieceAuthProperty | undefined,
  requireAuth: boolean,
): PiecePropertyMap {
  if (!requireAuth || isNil(auth)) {
    const newProps = Object.keys(props).reduce((acc, key) => {
      if (key !== 'auth') {
        acc[key] = props[key];
      }
      return acc;
    }, {} as PiecePropertyMap);
    return newProps;
  }
  return {
    ...props,
    ...spreadIfDefined('auth', auth),
  };
}

function buildInputSchemaForStep(
  type: ActionType | TriggerType,
  piece: PieceMetadata | null,
  actionNameOrTriggerName: string,
): TSchema {
  switch (type) {
    case ActionType.PIECE: {
      if (
        piece &&
        actionNameOrTriggerName &&
        piece.actions[actionNameOrTriggerName]
      ) {
        return formUtils.buildSchema(
          addAuthToPieceProps(
            piece.actions[actionNameOrTriggerName].props,
            piece.auth,
            piece.actions[actionNameOrTriggerName].requireAuth,
          ),
        );
      }
      return Type.Object({});
    }
    case TriggerType.PIECE: {
      if (
        piece &&
        actionNameOrTriggerName &&
        piece.triggers[actionNameOrTriggerName]
      ) {
        return formUtils.buildSchema(
          addAuthToPieceProps(
            piece.triggers[actionNameOrTriggerName].props,
            piece.auth,
            piece.triggers[actionNameOrTriggerName].requireAuth ?? true,
          ),
        );
      }
      return Type.Object({});
    }
    default:
      throw new Error('Unsupported type: ' + type);
  }
}

function buildConnectionSchema(
  piece: PieceMetadataModelSummary | PieceMetadataModel,
) {
  const auth = piece.auth;
  if (isNil(auth)) {
    return Type.Object({
      request: Type.Composite([
        Type.Omit(UpsertAppConnectionRequestBody, ['externalId']),
      ]),
    });
  }
  const connectionSchema = Type.Object({
    externalId: Type.String({
      pattern: '^[A-Za-z0-9_\\-@\\+\\.]*$',
      minLength: 1,
      errorMessage: t('Name can only contain letters, numbers and underscores'),
    }),
  });

  switch (auth.type) {
    case PropertyType.SECRET_TEXT:
      return Type.Object({
        request: Type.Composite([
          Type.Omit(UpsertSecretTextRequest, ['externalId', 'displayName']),
          connectionSchema,
        ]),
      });
    case PropertyType.BASIC_AUTH:
      return Type.Object({
        request: Type.Composite([
          Type.Omit(UpsertBasicAuthRequest, ['externalId', 'displayName']),
          connectionSchema,
        ]),
      });
    case PropertyType.CUSTOM_AUTH:
      return Type.Object({
        request: Type.Composite([
          Type.Omit(UpsertCustomAuthRequest, [
            'externalId',
            'value',
            'displayName',
          ]),
          connectionSchema,
          Type.Object({
            value: Type.Object({
              props: formUtils.buildSchema(
                (piece.auth as CustomAuthProperty<any>).props,
              ),
            }),
          }),
        ]),
      });
    case PropertyType.OAUTH2:
      return Type.Object({
        request: Type.Composite([
          Type.Omit(
            Type.Union([
              UpsertOAuth2Request,
              UpsertCloudOAuth2Request,
              UpsertPlatformOAuth2Request,
              UpsertKhubOAuth2Request,
            ]),
            ['externalId', 'displayName'],
          ),
          connectionSchema,
        ]),
      });
    case PropertyType.SWAGGER_FETCH_TOKEN_AUTH:
      return Type.Object({
        request: Type.Composite([
          Type.Omit(UpsertSwaggerFetchTokenAuthRequest, [
            'externalId',
            'value',
            'displayName',
          ]),
          connectionSchema,
          Type.Object({
            value: Type.Object({
              props: formUtils.buildSchema(
                (piece.auth as SwaggerFetchTokenAuthProperty<true, any>).props,
              ),
            }),
          }),
        ]),
      });
    default:
      return Type.Object({
        request: Type.Composite([
          Type.Omit(UpsertAppConnectionRequestBody, [
            'externalId',
            'displayName',
          ]),
          connectionSchema,
        ]),
      });
  }
}

export const formUtils = {
  buildPieceDefaultValue: (
    selectedStep: Action | Trigger,
    piece: PieceMetadata | null | undefined,
    includeCurrentInput: boolean,
  ): Action | Trigger => {
    const { type } = selectedStep;
    const defaultErrorOptions = {
      continueOnFailure: {
        value:
          selectedStep.settings.errorHandlingOptions?.continueOnFailure
            ?.value ?? false,
      },
      retryOnFailure: {
        value:
          selectedStep.settings.errorHandlingOptions?.retryOnFailure?.value ??
          false,
      },
    };
    const defaultExecutionOptions = {
      executeOnce: {
        value:
          selectedStep.settings.executionOptions?.executeOnce?.value ?? false,
      },
    };
    switch (type) {
      case ActionType.LOOP_ON_ITEMS:
        return {
          ...selectedStep,
          settings: {
            ...selectedStep.settings,
            items: selectedStep.settings.items ?? '',
          },
        };
      case ActionType.ROUTER:
        return {
          ...selectedStep,
        };
      case ActionType.CODE: {
        const defaultCode = `export const code = async (inputs) => {
  return true;
};`;
        return {
          ...selectedStep,
          settings: {
            ...selectedStep.settings,
            sourceCode: {
              code: selectedStep.settings.sourceCode.code ?? defaultCode,
              packageJson: selectedStep.settings.sourceCode.packageJson ?? '{}',
            },
            errorHandlingOptions: defaultErrorOptions,
            executionOptions: defaultExecutionOptions,
          },
        };
      }
      case ActionType.PIECE: {
        const actionName = selectedStep?.settings?.actionName;
        const requireAuth = isNil(actionName)
          ? false
          : piece?.actions?.[actionName]?.requireAuth ?? true;

        const actionPropsWithoutAuth = isNil(actionName)
          ? {}
          : piece?.actions?.[actionName]?.props ?? {};
        const props = addAuthToPieceProps(
          actionPropsWithoutAuth,
          piece?.auth,
          requireAuth,
        );
        const input = (selectedStep?.settings?.input ?? {}) as Record<
          string,
          unknown
        >;
        const defaultValues = getDefaultValueForStep(
          props ?? {},
          includeCurrentInput ? input : {},
        );
        return {
          ...selectedStep,
          settings: {
            ...selectedStep.settings,
            input: defaultValues,
            errorHandlingOptions: defaultErrorOptions,
            executionOptions: defaultExecutionOptions,
          },
        };
      }
      case TriggerType.PIECE: {
        const triggerName = selectedStep?.settings?.triggerName;
        const requireAuth = isNil(triggerName)
          ? false
          : piece?.triggers?.[triggerName]?.requireAuth ?? true;

        const triggerPropsWithoutAuth = isNil(triggerName)
          ? {}
          : piece?.triggers?.[triggerName]?.props ?? {};
        const props = addAuthToPieceProps(
          triggerPropsWithoutAuth,
          piece?.auth,
          requireAuth,
        );
        const input = (selectedStep?.settings?.input ?? {}) as Record<
          string,
          unknown
        >;
        const defaultValues = getDefaultValueForStep(
          props ?? {},
          includeCurrentInput ? input : {},
        );

        return {
          ...selectedStep,
          settings: {
            ...selectedStep.settings,
            input: defaultValues,
          },
        };
      }
      default:
        throw new Error('Unsupported type: ' + type);
    }
  },
  buildPieceSchema: (
    type: ActionType | TriggerType,
    actionNameOrTriggerName: string,
    piece: PieceMetadataModel | null,
  ) => {
    switch (type) {
      case ActionType.LOOP_ON_ITEMS:
        return Type.Composite([
          LoopOnItemsActionSchema,
          Type.Object({
            settings: Type.Object({
              items: Type.String({
                minLength: 1,
              }),
            }),
          }),
        ]);
      case ActionType.ROUTER:
        return Type.Intersect([
          Type.Omit(RouterActionSchema, ['settings']),
          Type.Object({
            settings: Type.Object({
              branches: RouterBranchesSchema(true),
              executionType: Type.Enum(RouterExecutionType),
              inputUiInfo: SampleDataSetting,
            }),
          }),
        ]);
      case ActionType.CODE:
        return CodeActionSchema;
      case ActionType.PIECE: {
        return Type.Composite([
          Type.Omit(PieceActionSchema, ['settings']),
          Type.Object({
            settings: Type.Composite([
              Type.Omit(PieceActionSettings, ['input', 'actionName']),
              Type.Object({
                actionName: Type.String({
                  minLength: 1,
                }),
                input: buildInputSchemaForStep(
                  type,
                  piece,
                  actionNameOrTriggerName,
                ),
              }),
            ]),
          }),
        ]);
      }
      case TriggerType.PIECE: {
        return Type.Composite([
          Type.Omit(PieceTrigger, ['settings']),
          Type.Object({
            settings: Type.Composite([
              Type.Omit(PieceTriggerSettings, ['input', 'triggerName']),
              Type.Object({
                triggerName: Type.String({
                  minLength: 1,
                }),
                input: buildInputSchemaForStep(
                  type,
                  piece,
                  actionNameOrTriggerName,
                ),
              }),
            ]),
          }),
        ]);
      }
      default: {
        throw new Error('Unsupported type: ' + type);
      }
    }
  },
  buildSchema: (props: PiecePropertyMap) => {
    const entries = Object.entries(props);
    const nullableType: TSchema[] = [Type.Null(), Type.Undefined()];
    const nonNullableUnknownPropType = Type.Not(
      Type.Union(nullableType),
      Type.Unknown(),
    );
    const propsSchema: Record<string, TSchema> = {};
    for (const [name, property] of entries) {
      switch (property.type) {
        case PropertyType.MARKDOWN:
          propsSchema[name] = Type.Optional(
            Type.Union([
              Type.Null(),
              Type.Undefined(),
              Type.Never(),
              Type.Unknown(),
            ]),
          );
          break;
        case PropertyType.SHORT_TEXT:
        case PropertyType.LONG_TEXT:
          propsSchema[name] = Type.String({
            minLength: property.required ? 1 : undefined,
            maxLength: property.maxLength,
          });
          break;
        case PropertyType.DATE_TIME:
        case PropertyType.FILE:
          propsSchema[name] = Type.String({
            minLength: property.required ? 1 : undefined,
          });
          break;
        case PropertyType.CHECKBOX:
          propsSchema[name] = Type.Union([
            Type.Boolean({ defaultValue: false }),
            Type.String({
              minLength: property.required ? 1 : undefined,
            }),
          ]);
          break;
        case PropertyType.NUMBER:
          // Because it could be a variable
          propsSchema[name] = Type.Union([
            Type.String({
              minLength: property.required ? 1 : undefined,
            }),
            Type.Number(),
          ]);
          break;
        case PropertyType.STATIC_DROPDOWN:
          propsSchema[name] = nonNullableUnknownPropType;
          break;
        case PropertyType.DROPDOWN:
          propsSchema[name] = nonNullableUnknownPropType;
          break;
        case PropertyType.BASIC_AUTH:
        case PropertyType.CUSTOM_AUTH:
        case PropertyType.SECRET_TEXT:
        case PropertyType.SWAGGER_FETCH_TOKEN_AUTH:
        case PropertyType.OAUTH2:
          // Only accepts connections variable.
          propsSchema[name] = Type.Union([
            Type.String({
              pattern: CONNECTION_REGEX,
              minLength: property.required ? 1 : undefined,
            }),
            Type.String({
              minLength: property.required ? 1 : undefined,
            }),
          ]);
          break;
        case PropertyType.ARRAY: {
          const arraySchema = isNil(property.properties)
            ? Type.String({
                minLength: property.required ? 1 : undefined,
              })
            : formUtils.buildSchema(property.properties);
          propsSchema[name] = Type.Union([
            Type.Array(arraySchema, {
              minItems: property.required ? 1 : undefined,
            }),
            Type.String({
              minLength: property.required ? 1 : undefined,
            }),
          ]);
          break;
        }
        case PropertyType.OBJECT:
          propsSchema[name] = Type.Union([
            Type.Record(Type.String(), Type.Any()),
            Type.String({
              minLength: property.required ? 1 : undefined,
            }),
          ]);
          break;
        case PropertyType.JSON:
          propsSchema[name] = Type.Union([
            Type.Record(Type.String(), Type.Any()),
            Type.Array(Type.Any()),
            Type.String({
              minLength: property.required ? 1 : undefined,
            }),
          ]);
          break;
        case PropertyType.MULTI_SELECT_DROPDOWN:
        case PropertyType.STATIC_MULTI_SELECT_DROPDOWN:
          propsSchema[name] = Type.Union([
            Type.Array(Type.Any()),
            Type.String({
              minLength: property.required ? 1 : undefined,
            }),
          ]);
          break;
        case PropertyType.DYNAMIC:
          propsSchema[name] = Type.Record(Type.String(), Type.Any());
          break;
      }

      //optional array is checked against its children
      if (!property.required && property.type !== PropertyType.ARRAY) {
        propsSchema[name] = Type.Optional(
          Type.Union(
            isEmpty(propsSchema[name])
              ? [Type.Any(), ...nullableType]
              : [propsSchema[name], ...nullableType],
          ),
        );
      }
    }
    return Type.Object(propsSchema);
  },
  getDefaultValueForStep,
  buildConnectionSchema,
};

export function getDefaultValueForStep(
  props: PiecePropertyMap | OAuth2Props,
  existingInput: Record<string, unknown>,
): Record<string, unknown> {
  const defaultValues: Record<string, unknown> = {};
  const entries = Object.entries(props);
  for (const [name, property] of entries) {
    switch (property.type) {
      case PropertyType.CHECKBOX:
        defaultValues[name] =
          existingInput[name] ?? property.defaultValue ?? false;
        break;
      case PropertyType.ARRAY:
        defaultValues[name] =
          existingInput[name] ?? property.defaultValue ?? [];
        break;
      case PropertyType.MARKDOWN:
      case PropertyType.DATE_TIME:
      case PropertyType.SHORT_TEXT:
      case PropertyType.LONG_TEXT:
      case PropertyType.FILE:
      case PropertyType.STATIC_DROPDOWN:
      case PropertyType.DROPDOWN:
      case PropertyType.BASIC_AUTH:
      case PropertyType.CUSTOM_AUTH:
      case PropertyType.SECRET_TEXT:
      case PropertyType.SWAGGER_FETCH_TOKEN_AUTH:
      case PropertyType.OAUTH2: {
        defaultValues[name] = existingInput[name] ?? property.defaultValue;
        break;
      }
      case PropertyType.JSON: {
        defaultValues[name] = existingInput[name] ?? property.defaultValue;
        break;
      }
      case PropertyType.NUMBER: {
        defaultValues[name] = existingInput[name] ?? property.defaultValue;
        break;
      }
      case PropertyType.MULTI_SELECT_DROPDOWN:
        defaultValues[name] = existingInput[name] ?? property.defaultValue;
        break;
      case PropertyType.STATIC_MULTI_SELECT_DROPDOWN:
        defaultValues[name] = existingInput[name] ?? property.defaultValue;
        break;
      case PropertyType.OBJECT:
      case PropertyType.DYNAMIC:
        defaultValues[name] =
          existingInput[name] ?? property.defaultValue ?? {};
        break;
    }
  }
  return defaultValues;
}

type UseDynamicPropertiesOptions<T = unknown> = {
  propertyName: string;
  onLoad: (value: T) => void;
};

export function useSavedDynamicProperties<T = unknown>({
  propertyName,
  onLoad,
}: UseDynamicPropertiesOptions<T>): void {
  const [flowVersion, selectedStep] = useBuilderStateContext((state) => [
    state.flowVersion,
    state.selectedStep,
  ]);
  const form = useFormContext<Action | Trigger>();

  useEffect(() => {
    const { settings } = form.getValues();
    const actionOrTriggerName = settings.actionName ?? settings.triggerName;
    const { pieceName } = settings;
    const dynamicKey = buildDynamicPropertiesKey({
      flowId: flowVersion.flowId,
      pieceName,
      stepName: String(selectedStep),
      actionOrTriggerName,
      propName: propertyName,
    });

    if (flowVersion.dynamicPropertiesValues?.[dynamicKey]) {
      onLoad(flowVersion.dynamicPropertiesValues[dynamicKey] as T);
    }
  }, []);
}

export const shouldHideFormPropertyByPiece = (
  propertyName: string,
  pieceName: string,
  typeValue: FormInputType,
) => {
  switch (pieceName) {
    case '@activepieces/piece-forms': {
      if (propertyName === 'defaultValue') {
        return [
          FormInputType.TOGGLE,
          FormInputType.FILE,
          FormInputType.DATE,
        ].includes(typeValue);
      }
      if (propertyName === 'description') {
        return typeValue === FormInputType.HIDDEN;
      }
      return false;
    }

    default:
      return false;
  }
};

type MaxLengthErrorProps = {
  property: PieceProperty;
  field: ControllerRenderProps<Record<string, any>, string>;
  propertyName: string;
};

export const DisplayMaxLengthError = ({
  property,
  field,
  propertyName,
}: MaxLengthErrorProps) => {
  const isTextType =
    property.type === PropertyType.SHORT_TEXT ||
    property.type === PropertyType.LONG_TEXT;

  const hasError =
    isTextType &&
    property.maxLength &&
    field.value &&
    String(field.value).length > property.maxLength;

  if (!hasError) {
    return null;
  }

  return (
    <FormError
      formMessageId={`${propertyName}-maxlength-error`}
      className="mt-2"
    >
      {t('This field cannot exceed {count} characters', {
        count: property.maxLength,
      })}
    </FormError>
  );
};

export const allRefreshersHaveValue = (
  refreshers: string[],
  refresherValues: unknown[],
): boolean => {
  return refreshers.every((_, index) => {
    const value = refresherValues[index];
    return value !== undefined && value !== null && value !== '';
  });
};

export const isAuthRefresherMissing = (
  refreshers: string[],
  refresherValues: unknown[],
): boolean => {
  const authIndex = refreshers.indexOf('auth');
  return (
    authIndex !== -1 &&
    (refresherValues[authIndex] === undefined ||
      refresherValues[authIndex] === null ||
      refresherValues[authIndex] === '')
  );
};
